import React, { useState } from "react";
import { useField, useFormikContext } from "formik";

// material
import {
  Autocomplete,
  Container,
  FormControl,
  MenuItem,
  Typography,
  TextField,
  Stack
} from "@mui/material";

// components
import Page from "../../../components/Page";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DatePicker from "@mui/lab/DatePicker";

// ----------------------------------------------------------------------

const isValidDate = (d) => {
  return d instanceof Date && !isNaN(d);
};

const submissionStatusOption = [
  {
    value: "Under review",
    label: "Under review"
  },
  {
    value: "revise paper",
    label: "revise paper"
  }
];

export default function UploadFormPage2SecB(props) {
  const formikProps = useFormikContext();
  const [coAuthor, setCoAuthor] = useState(0);

  const {
    formField: {
      submissionStatus,
      submitDate,
      receivedDate,
      revisedDate,
      articleTitle,
      manuscriptID,
      typeManuscript,
      journal,
      subjectAreaCategory,
      journalRanking,
      authors,
      firstAuthorName,
      correspondingAuthorName,
      coauthorAbroadPublication,
      coauthorName,
      coResearcherUniversityInstituteName,
      coResearcherUniversityInstituteRanking,
      supportedSecondCenturyFund,
      uploadManuscript,
      documentsShowingsSubmission,
      attachEvidenceTier1Paper,
      attachEvidenceUniversityInstituteRanking,
      additionalInformNoteSecB,
      additionalDocumentSecB
    }
  } = props;

  const [submissionStatusField, submissionStatusMeta] =
    useField(submissionStatus);
  const [submitDateField, submitDateMeta] = useField(submitDate);
  const [receivedDateField, receivedDateMeta] = useField(receivedDate);
  const [revisedDateField, revisedDateMeta] = useField(revisedDate);
  const [articleTitleField, articleTitleMeta] = useField(articleTitle);
  const [manuscriptIDField, manuscriptIDMeta] = useField(manuscriptID);
  const [typeManuscriptField, typeManuscriptMeta] = useField(typeManuscript);
  const [journalField, journalMeta] = useField(journal);
  const [subjectAreaCategoryField, subjectAreaCategoryMeta] =
    useField(subjectAreaCategory);
  const [journalRankingField, journalRankingMeta] = useField(journalRanking);
  const [authorsField, authorsMeta] = useField(authors);
  const [firstAuthorNameField, firstAuthorNameMeta] = useField(firstAuthorName);
  const [correspondingAuthorNameField, correspondingAuthorNameMeta] = useField(
    correspondingAuthorName
  );
  const [coauthorAbroadPublicationField, coauthorAbroadPublicationMeta] =
    useField(coauthorAbroadPublication);
  const [coauthorNameField, coauthorNameMeta] = useField(coauthorName);
  const [
    coResearcherUniversityInstituteNameField,
    coResearcherUniversityInstituteNameMeta
  ] = useField(coResearcherUniversityInstituteName);
  const [
    coResearcherUniversityInstituteRankingField,
    coResearcherUniversityInstituteRankingMeta
  ] = useField(coResearcherUniversityInstituteRanking);
  const [supportedSecondCenturyFundField, supportedSecondCenturyFundMeta] =
    useField(supportedSecondCenturyFund);
  const [uploadManuscriptField, uploadManuscriptMeta] =
    useField(uploadManuscript);
  const [documentsShowingsSubmissionField, documentsShowingsSubmissionMeta] =
    useField(documentsShowingsSubmission);
  const [attachEvidenceTier1PaperField, attachEvidenceTier1PaperMeta] =
    useField(attachEvidenceTier1Paper);
  const [
    attachEvidenceUniversityInstituteRankingField,
    attachEvidenceUniversityInstituteRankingMeta
  ] = useField(attachEvidenceUniversityInstituteRanking);
  const [additionalInformNoteSecBField, additionalInformNoteSecBMeta] =
    useField(additionalInformNoteSecB);
  const [additionalDocumentSecBField, additionalDocumentSecBMeta] = useField(
    additionalDocumentSecB
  );

  return (
    <Page title="UploadFormPage2SecB">
      <Container maxWidth="xl">
        <Stack
          direction="column"
          justifyContent="space-evenly"
          alignItems="stretch"
          spacing={4}
        >
          <Stack direction="row" spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {submissionStatus.label}
              </Typography>
              <Autocomplete
                freeSolo
                options={submissionStatusOption}
                onSelect={(e) =>
                  formikProps.setFieldValue(
                    submissionStatus.name,
                    e.target.value
                  )
                }
                value={submissionStatusField.value}
                isOptionEqualToValue={(option, value) => option.label === value}
                renderInput={(params) => (
                  <TextField
                    onChange={(e) => submissionStatusField.onChange(e)}
                    name={submissionStatusField.name}
                    error={Boolean(
                      submissionStatusMeta.touched && submissionStatusMeta.error
                    )}
                    helperText={
                      submissionStatusMeta.touched && submissionStatusMeta.error
                    }
                    {...params}
                  />
                )}
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2">{submitDate.label}</Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  inputFormat="dd/MM/yyyy"
                  name={submitDateField.name}
                  value={submitDateField.value}
                  fullWidth
                  onChange={(date) => {
                    if (isValidDate(date)) {
                      formikProps.setFieldValue(
                        submitDateField.name,
                        date.toISOString()
                      );
                    } else {
                      formikProps.setFieldValue(submitDateField.name, date);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={Boolean(
                        submitDateMeta.touched && submitDateMeta.error
                      )}
                      helperText={
                        submitDateMeta.touched && submitDateMeta.error
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">{receivedDate.label}</Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  inputFormat="dd/MM/yyyy"
                  name={receivedDateField.name}
                  value={receivedDateField.value}
                  fullWidth
                  onChange={(date) => {
                    if (isValidDate(date)) {
                      formikProps.setFieldValue(
                        receivedDateField.name,
                        date.toISOString()
                      );
                    } else {
                      formikProps.setFieldValue(receivedDateField.name, date);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={Boolean(
                        receivedDateMeta.touched && receivedDateMeta.error
                      )}
                      helperText={
                        receivedDateMeta.touched && receivedDateMeta.error
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2">{revisedDate.label}</Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  inputFormat="dd/MM/yyyy"
                  name={revisedDateField.name}
                  value={revisedDateField.value}
                  fullWidth
                  onChange={(date) => {
                    if (isValidDate(date)) {
                      formikProps.setFieldValue(
                        revisedDateField.name,
                        date.toISOString()
                      );
                    } else {
                      formikProps.setFieldValue(revisedDateField.name, date);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={Boolean(
                        revisedDateMeta.touched && revisedDateMeta.error
                      )}
                      helperText={
                        revisedDateMeta.touched && revisedDateMeta.error
                      }
                    />
                  )}
                />
              </LocalizationProvider>
            </FormControl>
          </Stack>

          <FormControl fullWidth>
            <Typography variant="subtitle2">{articleTitle.label}</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              name={articleTitleField.name}
              value={articleTitleField.value}
              onChange={(e) => articleTitleField.onChange(e)}
              error={Boolean(
                articleTitleMeta.touched && articleTitleMeta.error
              )}
              helperText={articleTitleMeta.touched && articleTitleMeta.error}
            />
          </FormControl>

          <Stack direction="row" spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">{manuscriptID.label}</Typography>
              <TextField
                name={manuscriptIDField.name}
                value={manuscriptIDField.value}
                onChange={(e) => manuscriptIDField.onChange(e)}
                error={Boolean(
                  manuscriptIDMeta.touched && manuscriptIDMeta.error
                )}
                helperText={manuscriptIDMeta.touched && manuscriptIDMeta.error}
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {typeManuscript.label}
              </Typography>
              <TextField
                name={typeManuscriptField.name}
                value={typeManuscriptField.value}
                onChange={(e) => typeManuscriptField.onChange(e)}
                error={Boolean(
                  typeManuscriptMeta.touched && typeManuscriptMeta.error
                )}
                helperText={
                  typeManuscriptMeta.touched && typeManuscriptMeta.error
                }
              />
            </FormControl>
          </Stack>

          <FormControl fullWidth>
            <Typography variant="subtitle2">{journal.label}</Typography>
            <TextField
              name={journalField.name}
              value={journalField.value}
              onChange={(e) => journalField.onChange(e)}
              error={Boolean(journalMeta.touched && journalMeta.error)}
              helperText={journalMeta.touched && journalMeta.error}
            />
          </FormControl>

          <Stack direction="row" spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {subjectAreaCategory.label}
              </Typography>
              <TextField
                name={subjectAreaCategoryField.name}
                value={subjectAreaCategoryField.value}
                onChange={(e) => subjectAreaCategoryField.onChange(e)}
                error={Boolean(
                  subjectAreaCategoryMeta.touched &&
                    subjectAreaCategoryMeta.error
                )}
                helperText={
                  subjectAreaCategoryMeta.touched &&
                  subjectAreaCategoryMeta.error
                }
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {journalRanking.label}
              </Typography>
              <TextField
                select
                variant="outlined"
                name={journalRankingField.name}
                value={journalRankingField.value}
                onChange={(e) => journalRankingField.onChange(e)}
                error={Boolean(
                  journalRankingMeta.touched && journalRankingMeta.error
                )}
                helperText={
                  journalRankingMeta.touched && journalRankingMeta.error
                }
              >
                <MenuItem value={"Tier 1"}>Tier 1</MenuItem>
                <MenuItem value={"Q1"}>Q1</MenuItem>
              </TextField>
            </FormControl>
          </Stack>

          <FormControl fullWidth>
            <Typography variant="subtitle2">{authors.label}</Typography>
            <TextField
              name={authorsField.name}
              value={authorsField.value}
              onChange={(e) => authorsField.onChange(e)}
              error={Boolean(authorsMeta.touched && authorsMeta.error)}
              helperText={authorsMeta.touched && authorsMeta.error}
            />
          </FormControl>

          <Stack direction="row" spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {firstAuthorName.label}
              </Typography>
              <TextField
                name={firstAuthorNameField.name}
                value={firstAuthorNameField.value}
                onChange={(e) => firstAuthorNameField.onChange(e)}
                error={Boolean(
                  firstAuthorNameMeta.touched && firstAuthorNameMeta.error
                )}
                helperText={
                  firstAuthorNameMeta.touched && firstAuthorNameMeta.error
                }
              />
            </FormControl>

            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {correspondingAuthorName.label}
              </Typography>
              <TextField
                name={correspondingAuthorNameField.name}
                value={correspondingAuthorNameField.value}
                onChange={(e) => correspondingAuthorNameField.onChange(e)}
                error={Boolean(
                  correspondingAuthorNameMeta.touched &&
                    correspondingAuthorNameMeta.error
                )}
                helperText={
                  correspondingAuthorNameMeta.touched &&
                  correspondingAuthorNameMeta.error
                }
              />
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={6}>
            <FormControl fullWidth>
              <Typography variant="subtitle2">
                {coauthorAbroadPublication.label}
              </Typography>
              <TextField
                select
                variant="outlined"
                name={coauthorAbroadPublicationField.name}
                value={coauthorAbroadPublicationField.value}
                onChange={(e) => {
                  setCoAuthor(e.target.value);
                  coauthorAbroadPublicationField.onChange(e);
                }}
                error={Boolean(
                  coauthorAbroadPublicationMeta.touched &&
                    coauthorAbroadPublicationMeta.error
                )}
                helperText={
                  coauthorAbroadPublicationMeta.touched &&
                  coauthorAbroadPublicationMeta.error
                }
              >
                <MenuItem value={1}>Yes, I have</MenuItem>
                <MenuItem value={0}>No, I have not</MenuItem>
              </TextField>
            </FormControl>

            {coAuthor === 1 && (
              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {coauthorName.label}
                </Typography>
                <TextField
                  placeholder="e.g. Professor Dr. Richard"
                  name={coauthorNameField.name}
                  value={coauthorNameField.value}
                  onChange={(e) => coauthorNameField.onChange(e)}
                  error={Boolean(
                    coauthorNameMeta.touched && coauthorNameMeta.error
                  )}
                  helperText={
                    coauthorNameMeta.touched && coauthorNameMeta.error
                  }
                />
              </FormControl>
            )}
          </Stack>

          {coAuthor === 1 && (
            <Stack direction="row" spacing={6}>
              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {coResearcherUniversityInstituteName.label}
                </Typography>
                <TextField
                  name={coResearcherUniversityInstituteNameField.name}
                  value={coResearcherUniversityInstituteNameField.value}
                  onChange={(e) =>
                    coResearcherUniversityInstituteNameField.onChange(e)
                  }
                  error={Boolean(
                    coResearcherUniversityInstituteNameMeta.touched &&
                      coResearcherUniversityInstituteNameMeta.error
                  )}
                  helperText={
                    coResearcherUniversityInstituteNameMeta.touched &&
                    coResearcherUniversityInstituteNameMeta.error
                  }
                  placeholder="e.g. Cambridge University"
                />
              </FormControl>

              <FormControl fullWidth>
                <Typography variant="subtitle2">
                  {coResearcherUniversityInstituteRanking.label}
                </Typography>
                <TextField
                  select
                  variant="outlined"
                  name={coResearcherUniversityInstituteRankingField.name}
                  value={coResearcherUniversityInstituteRankingField.value}
                  onChange={(e) =>
                    coResearcherUniversityInstituteRankingField.onChange(e)
                  }
                  error={Boolean(
                    coResearcherUniversityInstituteRankingMeta.touched &&
                      coResearcherUniversityInstituteRankingMeta.error
                  )}
                  helperText={
                    coResearcherUniversityInstituteRankingMeta.touched &&
                    coResearcherUniversityInstituteRankingMeta.error
                  }
                >
                  <MenuItem value={"Top 100 University_(QS or THE)"}>
                    Top 100 University_(QS or THE)
                  </MenuItem>
                  <MenuItem value={"Top 100 subject University_(QS or THE)"}>
                    Top 100 subject University_(QS or THE)
                  </MenuItem>
                  <MenuItem value={"Top institute"}>Top institute</MenuItem>
                  <MenuItem value={"Other"}>Other</MenuItem>
                </TextField>
              </FormControl>
            </Stack>
          )}

          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {supportedSecondCenturyFund.label}
            </Typography>
            <TextField
              select
              variant="outlined"
              name={supportedSecondCenturyFundField.name}
              value={supportedSecondCenturyFundField.value}
              onChange={(e) => supportedSecondCenturyFundField.onChange(e)}
              error={Boolean(
                supportedSecondCenturyFundMeta.touched &&
                  supportedSecondCenturyFundMeta.error
              )}
              helperText={
                supportedSecondCenturyFundMeta.touched &&
                supportedSecondCenturyFundMeta.error
              }
            >
              <MenuItem value={"Yes, I do."}>Yes, I do.</MenuItem>
              <MenuItem value={"No, I do not."}>No, I do not.</MenuItem>
            </TextField>
          </FormControl>

          <FormControl>
            <Typography variant="subtitle2">
              {uploadManuscript.label}
            </Typography>
            <TextField
              inputProps={{
                accept:
                  "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
              }}
              type="file"
              name={uploadManuscriptField.name}
              onChange={(event) => {
                formikProps.setFieldValue(
                  `uploadManuscript.file`,
                  event.currentTarget.files[0]
                );
              }}
              error={Boolean(
                uploadManuscriptMeta.touched && uploadManuscriptMeta.error
              )}
              helperText={
                uploadManuscriptMeta.touched && uploadManuscriptMeta.error
              }
            />
          </FormControl>

          <FormControl>
            <Typography variant="subtitle2">
              {documentsShowingsSubmission.label}
            </Typography>
            <TextField
              inputProps={{
                accept:
                  "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
              }}
              type="file"
              name={documentsShowingsSubmissionField.name}
              onChange={(event) => {
                formikProps.setFieldValue(
                  `documentsShowingsSubmission.file`,
                  event.currentTarget.files[0]
                );
              }}
              error={Boolean(
                documentsShowingsSubmissionMeta.touched &&
                  documentsShowingsSubmissionMeta.error
              )}
              helperText={
                documentsShowingsSubmissionMeta.touched &&
                documentsShowingsSubmissionMeta.error
              }
            />
            <Typography>
              For those who have already submitted a research paper, but have
              not accepted yet.
            </Typography>
          </FormControl>

          <FormControl>
            <Typography variant="subtitle2">
              {attachEvidenceTier1Paper.label}
            </Typography>
            <TextField
              inputProps={{
                accept:
                  "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
              }}
              type="file"
              name={attachEvidenceTier1PaperField.name}
              onChange={(event) => {
                formikProps.setFieldValue(
                  `attachEvidenceTier1Paper.file`,
                  event.currentTarget.files[0]
                );
              }}
              error={Boolean(
                attachEvidenceTier1PaperMeta.touched &&
                  attachEvidenceTier1PaperMeta.error
              )}
              helperText={
                attachEvidenceTier1PaperMeta.touched &&
                attachEvidenceTier1PaperMeta.error
              }
            />
          </FormControl>

          {coAuthor === 1 && (
            <FormControl>
              <Typography variant="subtitle2">
                {attachEvidenceUniversityInstituteRanking.label}
              </Typography>
              <TextField
                inputProps={{
                  accept:
                    "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
                }}
                type="file"
                name={attachEvidenceUniversityInstituteRankingField.name}
                onChange={(event) => {
                  formikProps.setFieldValue(
                    `attachEvidenceUniversityInstituteRanking.file`,
                    event.currentTarget.files[0]
                  );
                }}
                error={Boolean(
                  attachEvidenceUniversityInstituteRankingMeta.touched &&
                    attachEvidenceUniversityInstituteRankingMeta.error
                )}
                helperText={
                  attachEvidenceUniversityInstituteRankingMeta.touched &&
                  attachEvidenceUniversityInstituteRankingMeta.error
                }
              />
            </FormControl>
          )}

          <FormControl fullWidth>
            <Typography variant="subtitle2">
              {additionalInformNoteSecB.label}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              name={additionalInformNoteSecBField.name}
              value={additionalInformNoteSecBField.value}
              onChange={(e) => additionalInformNoteSecBField.onChange(e)}
              error={Boolean(
                additionalInformNoteSecBMeta.touched &&
                  additionalInformNoteSecBMeta.error
              )}
              helperText={
                additionalInformNoteSecBMeta.touched &&
                additionalInformNoteSecBMeta.error
              }
            />
          </FormControl>

          <FormControl>
            <Typography variant="subtitle2">
              {additionalDocumentSecB.label}
            </Typography>
            <TextField
              inputProps={{
                accept:
                  "image/x-png,image/gif,image/jpeg,.doc,.docx,application/pdf"
              }}
              type="file"
              name={additionalDocumentSecBField.name}
              onChange={(event) => {
                formikProps.setFieldValue(
                  `additionalDocumentSecB.file`,
                  event.currentTarget.files[0]
                );
              }}
              error={Boolean(
                additionalDocumentSecBMeta.touched &&
                  additionalDocumentSecBMeta.error
              )}
              helperText={
                additionalDocumentSecBMeta.touched &&
                additionalDocumentSecBMeta.error
              }
            />
          </FormControl>
        </Stack>
      </Container>
    </Page>
  );
}
